import { Ok } from 'ts-results';

import type { Basis, Channel } from './channel';
import type { Device } from './device';
import type { Pulse } from './pulse';
import type { Atom } from './register';
import type { Result } from 'ts-results';
import type { ValidationError } from '~/util/validation';

type Uuid = string;

export type CmdAddPulse = Readonly<{
  kind: 'add_pulse';
  pulse: Pulse;
  // insert pulse to be before the pulse with following UUID. If null, insert pulse to end of list
  insertBefore: string | null;
}>;

export type CmdUpdatePulse = Readonly<{
  kind: 'update_pulse';
  uuid: Uuid;
  props: Partial<Omit<Pulse, 'uuid'>>;
}>;

export type CmdRemovePulse = Readonly<{
  kind: 'remove_pulse';
  uuid: Uuid;
}>;

export type CmdMovePulse = Readonly<{
  kind: 'move_pulse';
  uuid: Uuid;
  insertBefore: string | null;
}>;

export type CmdRename = Readonly<{
  kind: 'rename';
  name: string;
}>;

export type CmdAddAtom = Readonly<{
  kind: 'add_atom';
  atom: Atom;
  insertBefore: string | null;
}>;

export type CmdUpdateAtom = Readonly<{
  kind: 'update_atom';
  uuid: string;
  props: Partial<Omit<Atom, 'uuid'>>;
}>;

export type CmdRemoveAtom = Readonly<{
  kind: 'remove_atom';
  uuid: string;
}>;

export type CmdSetBasis = Readonly<{
  kind: 'set_basis';
  basis: Basis | null;
}>;

export type CmdSetDevice = Readonly<{
  kind: 'set_device';
  device: Device;
  channels: Channel[];
}>;

export type CmdUpdateDevice = Readonly<{
  kind: 'update_device';
  props: Partial<Device>;
}>;

export type CmdAddChannel = Readonly<{
  kind: 'add_channel';
  channel: Channel;
  insertBefore: string | null;
}>;

export type CmdUpdateChannel = Readonly<{
  kind: 'update_channel';
  uuid: Uuid;
  props: Partial<Channel>;
}>;

export type CmdMoveChannel = Readonly<{
  kind: 'move_channel';
  uuid: Uuid;
  insertBefore: Uuid | null;
}>;

export type CmdRemoveChannel = Readonly<{
  kind: 'remove_channel';
  uuid: Uuid;
}>;

export type ExperimentCommand =
  | CmdAddPulse
  | CmdUpdatePulse
  | CmdRemovePulse
  | CmdMovePulse
  | CmdRename
  | CmdAddAtom
  | CmdUpdateAtom
  | CmdRemoveAtom
  | CmdSetBasis
  | CmdSetDevice
  | CmdUpdateDevice
  | CmdAddChannel
  | CmdRemoveChannel
  | CmdUpdateChannel;

/**
 * Parse an unknown JS object into a list of `ExperimentCommand`s.
 *
 * Used for reading saved undo/redo stack from potentially incompatible localstorage data.
 */
export function parseExperimentCommandStack(
  jsValue: unknown
): Result<ExperimentCommand[][], ValidationError> {
  return Ok(jsValue as ExperimentCommand[][]);
  // BB: removed some Zod-based format validation here.
}
